/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import { useGSAP } from "@gsap/react";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import React, { RefObject, forwardRef, useRef, useState } from "react";
import ReactPlayer from "react-player/lazy";

import { BorderRadiuses } from "@/tokens/border";
import { SpacingStyleKey, spacingSets } from "@/tokens/configs/spacing_config";

import { useCombinedRefs } from "@/util/hooks/ref_hooks";
import { buildStylesByBreakpoint } from "@/util/style_util";

interface VideoProps extends React.ComponentProps<typeof ReactPlayer> {
    className?: SerializedStyles;
    controls?: boolean;
    loop?: boolean;
    marginBottom?: SpacingStyleKey;
    marginTop?: SpacingStyleKey;
    pauseOnViewportExit?: boolean;
    pauseOtherVideosOnPlay?: (reactPlayer: ReactPlayer) => void;
    playing?: boolean;
    playOnHover?: boolean;
    playOnViewportEntry?: boolean;
    poster?: string;
    url: string;
}

export const ExternalVideo = forwardRef<ReactPlayer, VideoProps>(
    ({ controls = true, loop = false, ...props }, ref) => {
        const {
            className,
            marginBottom,
            marginTop,
            pauseOnViewportExit,
            pauseOtherVideosOnPlay,
            playOnHover,
            playOnViewportEntry,
            poster,
            url,
            ...rest
        } = props;

        /**
         * Refs
         */
        const containerRef = useRef<HTMLDivElement>(null);

        const _localVideoRef = useRef<ReactPlayer>(null);

        const videoRef = useCombinedRefs(
            ref,
            _localVideoRef,
        ) as unknown as RefObject<ReactPlayer>;

        /**
         * State
         */
        const [isReady, setIsReady] = useState(false);

        /**
         * Effect Hooks
         */
        useGSAP(() => {
            if (isReady) {
                ScrollTrigger.create({
                    onEnter: () => {
                        if (playOnViewportEntry) {
                            togglePlay({ shouldPlay: true });
                        }
                    },
                    onEnterBack: () => {
                        if (playOnViewportEntry) {
                            togglePlay({ shouldPlay: true });
                        }
                    },
                    onLeave: () => {
                        if (pauseOnViewportExit) {
                            togglePlay({ shouldPlay: false });
                        }
                    },
                    onLeaveBack: () => {
                        if (pauseOnViewportExit) {
                            togglePlay({ shouldPlay: false });
                        }
                    },
                    trigger: containerRef.current,
                });
            }
        }, [playOnViewportEntry, pauseOnViewportExit, isReady]);

        /**
         * Helper Functions
         */
        const togglePlay = ({ shouldPlay }: { shouldPlay: boolean }) => {
            const _castedVideoEl =
                videoRef.current?.getInternalPlayer() as HTMLVideoElement;

            if (shouldPlay && _castedVideoEl) {
                const playPromise = _castedVideoEl.play();
                if (playPromise !== undefined) {
                    _castedVideoEl.setAttribute("controls", "true");
                }
            }

            if (!shouldPlay && _castedVideoEl) {
                _castedVideoEl.pause();
                _castedVideoEl.removeAttribute("controls");
            }
        };

        /**
         * Event Handlers
         */
        const handleOnMouseHover = () => {
            if (playOnHover) {
                togglePlay({ shouldPlay: true });
            }
        };

        /**
         * Styles
         */
        const marginTopStyles = marginTop
            ? buildStylesByBreakpoint("marginTop", spacingSets[marginTop])
            : undefined;

        const marginBottomStyles = marginBottom
            ? buildStylesByBreakpoint("marginBottom", spacingSets[marginBottom])
            : undefined;

        const videoWrapperStyles = css(
            marginTopStyles,
            marginBottomStyles,
            className,
        );

        const videoStyles = css({
            "& video": {
                borderRadius: BorderRadiuses["borderMedium"],
                height: "100%",
                objectFit: "cover",
                overflow: "hidden",
                width: "100%",
            },
        });

        /*
         * Rendering
         */
        if (url.includes("loom.com")) {
            return (
                <div css={videoWrapperStyles}>
                    <iframe
                        allowFullScreen
                        src={url}
                        style={{
                            aspectRatio: props.aspectRatio,
                            height: "100%",
                            width: "100%",
                        }}
                    />
                </div>
            );
        }
        return (
            <div
                css={videoWrapperStyles}
                ref={containerRef}
                onMouseEnter={handleOnMouseHover}
            >
                <ReactPlayer
                    config={{
                        file: {
                            attributes: {
                                poster,
                            },
                        },
                        vimeo: {
                            playerOptions: {
                                transparent: false,
                            },
                        },
                    }}
                    controls={controls}
                    css={videoStyles}
                    height="100%"
                    loop={loop}
                    muted={!controls}
                    playing={true}
                    playsinline={true}
                    preload="metadata"
                    ref={videoRef}
                    url={url}
                    width="100%"
                    onPlay={() => {
                        if (pauseOtherVideosOnPlay) {
                            pauseOtherVideosOnPlay(
                                videoRef as unknown as ReactPlayer,
                            );
                        }
                    }}
                    onReady={() => {
                        setIsReady(true);
                    }}
                    {...rest}
                />
            </div>
        );
    },
);

ExternalVideo.displayName = "ExternalVideo";
